import { useCallback, useEffect, useState, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import Select from "react-select";
import swal from "sweetalert";
import { useSelector, useDispatch } from "react-redux";
import { setVisibleColumns, setSortOrder, setSortBy } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import { MANAGE_PAGE_SEARCH_DEBOUNCE_MS } from "../../utils/managePageConstants";
import APIs from "../../Constants/apis";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import axiosInstance from "../../config/axiosApi/axiosApi";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import PettyCashHeadModal from "./PettyCashModel";
import { appSettings } from "../../app/appSettingsSlice";
import "./../order/ManageOrderFilters.css";

const ManagePettyCashHeads = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const profileData = useSelector((state) => state.auth.profileData);
  const isBranch = (profileData?.parentComID ?? 0) !== 0;
  const [search, setSearch] = useState("");
  const pageKey = "pettyCash";
  const defaultVisibleColumns = {
    active: true,
    updateDatetime: true,
  };
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "ID", label: "ID" };
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const dispatch = useDispatch();
  const [pettyCashHeads, setPettyCashHeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [headToEdit, setHeadToEdit] = useState(null);
  const lastSelectedIndex = useRef(null);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();



  const sortByOptions = [
    { value: "ID", label: "ID" },
    { value: "Name", label: "Name" },
    { value: "UpdateDatetime", label: "Last Updated" },
  ];

  const clearFilters = () => {
    setSearch("");
  };

  const handleHeadSubmit = (submittedData, isUpdate) => {
    if (isUpdate) {
      setPettyCashHeads((prevHeads) =>
        prevHeads.map((head) =>
          head.id === submittedData.ID
            ? { ...head, ...submittedData, updateDatetime: new Date(submittedData.updateDateTime) }
            : head
        )
      );
    } else {
      setPettyCashHeads((prevHeads) => [
        ...prevHeads,
        {
          id: submittedData.ID || Date.now(),
          name: submittedData.name,
          active: submittedData.active,
          compID: submittedData.compID,
          updateDatetime: new Date(submittedData.updateDateTime),
        },
      ]);
    }
    fetchPettyCashHeads();
  };

  const DeletePettyCashHead = async (head) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${head.name}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    setLoading(true);
    try {
      await axiosInstance.delete(APIs.DELETE_PETTY_CASH_HEAD, {
        params: {
          CompID,
          ID: head.id,
        },
      }).then((response) => {
        if (response.data.status) {
          setPettyCashHeads((prevHeads) =>
            prevHeads.filter((h) => h.id !== head.id)
          );
          showSuccessToast("Petty Cash Head is Deleted.");
        } else {
          showErrorToast(response.data.message);
        }
      });
      setLoading(false);
    } catch (err) {
      showErrorToast("Something went wrong.");
      setLoading(false);
    }
  };

  const handleSelectItem = (headId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (pettyCashHeads[i]?.id) {
            newSelected.add(pettyCashHeads[i].id);
          }
        }
      } else {
        if (newSelected.has(headId)) {
          newSelected.delete(headId);
        } else {
          newSelected.add(headId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(pettyCashHeads.map((item) => item.id));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No petty cash heads selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected petty cash heads?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((headId) =>
          axiosInstance.delete(APIs.DELETE_PETTY_CASH_HEAD, {
            params: {
              CompID,
              ID: headId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchPettyCashHeads();
      showSuccessToast(`${selectedItems.size} petty cash heads deleted successfully.`);
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to delete selected petty cash heads.");
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No petty cash heads selected for editing.");
      return;
    }
    showErrorToast("Bulk edit not implemented yet.");
  };

  const handleEdit = (head) => {
    setHeadToEdit(head);
    setIsModalOpen(true);
  };

  // New function to handle toggle switch change
  const handleToggleActive = async (head) => {
    const newActiveStatus = !head.active;

    // Optimistic update
    setPettyCashHeads((prevHeads) =>
      prevHeads.map((h) =>
        h.id === head.id ? { ...h, active: newActiveStatus } : h
      )
    );

    try {
      const response = await axiosInstance.post(APIs.UPDATE_PETTY_CASH_STATUS, {
        CompID,
        ID: head.id,
        Active: newActiveStatus,
      });

      if (response.data.status) {
        showSuccessToast(`Petty Cash Head ${newActiveStatus ? 'activated' : 'deactivated'} successfully.`);
        // Refetch to ensure consistency with backend
        fetchPettyCashHeads();
      } else {
        // Revert optimistic update on failure
        setPettyCashHeads((prevHeads) =>
          prevHeads.map((h) =>
            h.id === head.id ? { ...h, active: head.active } : h
          )
        );
        showErrorToast(response.data.message);
      }
    } catch (err) {
      // Revert optimistic update on error
      setPettyCashHeads((prevHeads) =>
        prevHeads.map((h) =>
          h.id === head.id ? { ...h, active: head.active } : h
        )
      );
      showErrorToast("Failed to update petty cash status.");
    }
  };

  const fetchPettyCashHeads = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_PETTY_CASH_HEADS, {
        params: {
          CompID,
          Search: search,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
        },
      });

      const headsData = response.data.data || response.data;

      if (Array.isArray(headsData)) {
        setPettyCashHeads(headsData);
      } else {
        setPettyCashHeads([]);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showErrorToast("Failed to fetch petty cash heads.");
    }
  }, [CompID, search, sortBy, sortOrder]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchPettyCashHeads();
    }, MANAGE_PAGE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [fetchPettyCashHeads]);

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
  }, [dispatch, pageKey]);

  return (
    <div>
      {loading && <Preloader />}
      <PettyCashHeadModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        onHeadSubmit={handleHeadSubmit}
        compID={CompID}
        headToEdit={headToEdit}
      />
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Petty Cash Heads</li>
          </ol>
          <h1 className="page-header mb-0">Manage Petty Cash Heads</h1>
        </div>
        {!isBranch && (
          <div className="ms-auto">
            <button
              className="btn px-4"
              onClick={() => {
                setHeadToEdit(null);
                setIsModalOpen(true);
              }}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: 'none',
                color: 'white',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: `0 2px 8px ${themeColor}40`
              }}
              onMouseEnter={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
              Add Petty Cash Head
            </button>
          </div>
        )}
      </div>
      <div className="mb-3 mt-2">
        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              {/* Enhanced Filter Section */}
              <div className="mb-4">
                <div
                  className="card filter-card"
                  style={{
                    position: "relative",
                    zIndex: 1,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: `0 4px 20px ${themeColor}40`
                  }}
                >
                  <div className="card-body p-3">
                    {/* Filter Controls */}
                    <div className="row g-3 align-items-center">
                      {/* Search Field */}
                      <div className="col-xl-4 col-lg-4 col-md-6">
                        <div className="search-input filter-control">
                          <input
                            type="text"
                            className="filter-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by name..."
                            style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              color: '#333'
                            }}
                          />
                        </div>
                      </div>

                      {/* Sort By Dropdown */}
                      <div className="col-xl-3 col-lg-3 col-md-6">
                        <div className="filter-control">
                          <Select
                            value={sortBy}
                            onChange={(val) => dispatch(setSortBy({ pageKey, sortBy: val }))}
                            options={sortByOptions}
                            styles={dropdownStyles}
                            placeholder="Sort By"
                            className="filter-dropdown"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuShouldBlockScroll={false}
                            menuShouldScrollIntoView={false}
                            closeMenuOnScroll={false}
                          />
                        </div>
                      </div>

                      {/* Sort Order Dropdown */}
                      <div className="col-xl-2 col-lg-2 col-md-6">
                        <div className="filter-control">
                          <SortOrderDropDownSelection
                            defaultValue={sortOrder}
                            onChange={(val) => dispatch(setSortOrder({ pageKey, sortOrder: val }))}
                          />
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="col-xl-3 col-lg-3 col-md-12">
                        <div className="d-flex gap-2">
                          <button
                            className="clear-btn"
                            onClick={clearFilters}
                            title="Clear all filters"
                          >
                            <i className="fa fa-times me-1"></i>
                            Filter
                          </button>

                          <ColumnSelection
                            visibleColumns={visibleColumns}
                            setVisibleColumns={(cols) =>
                              dispatch(setVisibleColumns({ pageKey, columns: cols }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {!isBranch && selectedItems.size > 0 && (
                <div className="mb-3">
                  <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between" style={{ border: "1px solid #dee2e6" }}>
                    <div className="d-flex align-items-center gap-3">
                      <span className="fw-bold text-primary">
                        {selectedItems.size} head{selectedItems.size > 1 ? 's' : ''} selected
                      </span>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleDeselectAll}
                      >
                        Clear Selection
                      </button>
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={handleBulkEdit}
                        title="Edit Selected Heads"
                      >
                        <i className="fa fa-edit me-1"></i>
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={handleBulkDelete}
                        title="Delete Selected Heads"
                      >
                        <i className="fa fa-trash me-1"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div
                className="table-responsive mt-3"
                style={{
                  minHeight: "200px",
                  maxHeight: "300px",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table table-hover">
                  <thead
                    className="bg-light"
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 5,
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <tr>
                      {!isBranch && (
                        <th className="pt-2 pb-2">
                          <input
                            type="checkbox"
                            onChange={handleSelectAll}
                            checked={
                              pettyCashHeads.length > 0 &&
                              selectedItems.size === pettyCashHeads.length
                            }
                          />
                        </th>
                      )}
                      <th className="pt-2 pb-2">Name</th>
                      {visibleColumns.active && (
                        <th className="pt-2 pb-2 text-center">Active</th>
                      )}
                      {visibleColumns.updateDatetime && (
                        <th className="pt-2 pb-2 text-center">Last Updated</th>
                      )}
                      {!isBranch && (
                        <th className="pt-2 pb-2 text-center">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={isBranch ? (2 + (visibleColumns.active ? 1 : 0) + (visibleColumns.updateDatetime ? 1 : 0)) : (3 + (visibleColumns.active ? 1 : 0) + (visibleColumns.updateDatetime ? 1 : 0))} className="text-center py-4">
                          <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <span className="ms-2">Loading...</span>
                        </td>
                      </tr>
                    ) : pettyCashHeads.length === 0 ? (
                      <tr>
                        <td colSpan={isBranch ? (2 + (visibleColumns.active ? 1 : 0) + (visibleColumns.updateDatetime ? 1 : 0)) : (3 + (visibleColumns.active ? 1 : 0) + (visibleColumns.updateDatetime ? 1 : 0))} className="text-center py-4">
                          <div className="text-muted">
                            <i className="fa fa-search fa-2x mb-2"></i>
                            <p className="mb-0">
                              {search.trim() ? "No petty cash heads found matching your search criteria." : "No petty cash heads available."}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      pettyCashHeads.map((head, index) => (
                        <tr key={head.id}>
                          {!isBranch && (
                            <td className="align-middle">
                              <input
                                type="checkbox"
                                checked={selectedItems.has(head.id)}
                                onChange={(e) =>
                                  handleSelectItem(head.id, index, e)
                                }
                                data-chkbox-shiftsel="type1"
                              />
                            </td>
                          )}
                          <td
                            className="align-middle"
                            onClick={!isBranch ? () => handleEdit(head) : undefined}
                            style={{ cursor: isBranch ? "default" : "pointer" }}
                          >
                            {head.name}
                          </td>
                          {visibleColumns.active && (
                            <td className="align-middle text-center">
                              <div
                                className={`toggle-switch ${head.active ? "active" : ""
                                  }`}
                              >
                                <label className="switch">
                                  <input
                                    type="checkbox"
                                    checked={head.active}
                                    onChange={() => handleToggleActive(head)} // Updated to use new function
                                  />
                                  <span
                                    className="slider round"
                                    style={{
                                      backgroundColor: head.active ? themeColor : '#ccc'
                                    }}
                                  ></span>
                                </label>
                              </div>
                            </td>
                          )}
                          {visibleColumns.updateDatetime && (
                            <td
                              className="align-middle text-center"
                              onClick={!isBranch ? () => handleEdit(head) : undefined}
                              style={{ cursor: isBranch ? "default" : "pointer" }}
                            >
                              {new Date(head.updateDatetime).toLocaleString()}
                            </td>
                          )}
                          {!isBranch && (
                            <td className="align-middle text-center">
                              <div className="d-flex justify-content-center gap-2">
                                {/* Edit Icon */}
                                <button
                                  onClick={() => handleEdit(head)}
                                  className="btn btn-sm btn-outline-primary"
                                  title="Edit Petty Cash Head"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: `1px solid ${themeColor}`,
                                    color: themeColor,
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = themeColor;
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = themeColor;
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                                </button>

                                {/* Delete Icon */}
                                <button
                                  onClick={() => DeletePettyCashHead(head)}
                                  className="btn btn-sm btn-outline-danger"
                                  title="Delete Petty Cash Head"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: '1px solid #dc3545',
                                    color: '#dc3545',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#dc3545';
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#dc3545';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagePettyCashHeads;